import { ClimateRegion, HeatingFuel } from './types';

// Every tunable constant for the electrification simulator lives here.
// Values are authored approximations of public data (EIA RECS seasonal
// patterns, NREL PVWatts production, national installer pricing surveys)
// intended for general-but-credible estimates, not quotes.

// Heating demand by climate region for the reference home, in therms/year.
// Scaled linearly by sqft relative to DEFAULT_HOME_SQFT.
export const ANNUAL_HEATING_THERMS_BY_REGION: Record<ClimateRegion, number> = {
  COLD: 850,
  HOT_DRY: 250,
  HOT_HUMID: 250,
  MARINE: 450,
  MIXED: 600,
};

// Federal residential purchase credits expired: the 25D ITC (solar/battery)
// and 25C (heat pumps/insulation) ended Dec 31, 2025. All federal incentive
// rates are zero so the simulator shows full purchase cost; state/utility
// incentives are not modeled. The incentive plumbing stays in place for
// future state programs.

// Tesla Powerwall 3 reference: 13.5 kWh usable, ~$15.5k installed.
export const BATTERY_GROSS_USD = 15500;
export const BATTERY_ITC_RATE = 0;
export const BATTERY_ROUND_TRIP_EFFICIENCY = 0.9;
export const BATTERY_USABLE_KWH = 13.5;

// Regional cooling demand curves (CDD-shaped), Jan-Dec, each sums to 1. Used
// with the heating curves to attribute above-baseload "excess" kWh between
// cooling and heating. Authoring invariant (tested): for every region and
// month, cooling weight + heating weight > 0.
export const COOLING_MONTH_WEIGHTS_BY_REGION: Record<ClimateRegion, number[]> = {
  COLD: [0, 0, 0, 0.02, 0.08, 0.18, 0.27, 0.25, 0.14, 0.05, 0.01, 0],
  HOT_DRY: [0.01, 0.01, 0.03, 0.06, 0.11, 0.16, 0.2, 0.19, 0.13, 0.07, 0.02, 0.01],
  HOT_HUMID: [0.02, 0.02, 0.04, 0.07, 0.1, 0.14, 0.17, 0.17, 0.13, 0.08, 0.04, 0.02],
  MARINE: [0, 0, 0.01, 0.03, 0.07, 0.14, 0.24, 0.24, 0.16, 0.08, 0.02, 0.01],
  MIXED: [0, 0, 0.02, 0.04, 0.09, 0.17, 0.25, 0.23, 0.13, 0.05, 0.01, 0.01],
};

export const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// Typical primary heating fuel by region (EIA RECS): the Northeast, Midwest,
// and coastal West heat mostly with gas/oil; the South and Southwest are
// largely all-electric (heat pumps). Used as the DEFAULT only — the user can
// still change it. MARINE defaults to GAS on population weight (coastal CA);
// the Pacific Northwest's electric heat is the minority.
export const DEFAULT_HEATING_FUEL_BY_REGION: Record<ClimateRegion, HeatingFuel> = {
  COLD: 'GAS',
  HOT_DRY: 'HEAT_PUMP',
  HOT_HUMID: 'HEAT_PUMP',
  MARINE: 'GAS',
  MIXED: 'GAS',
};

export const DEFAULT_HOME_SQFT = 1800;

// Fraction of an electric-heat home's annual kWh that goes to heating, per
// region (RECS-basis). Blends the EIA state usage shape toward a winter-peaked
// curve for homes that heat with electricity.
export const ELECTRIC_HEAT_SHARE_BY_REGION: Record<ClimateRegion, number> = {
  COLD: 0.5,
  HOT_DRY: 0.15,
  HOT_HUMID: 0.15,
  MARINE: 0.45,
  MIXED: 0.35,
};

export const FURNACE_AFUE_GAS = 0.9;
export const FURNACE_AFUE_OIL = 0.85;

export const GAS_USD_PER_THERM = 1.45;

// 25C heat pump credit expired Dec 31, 2025 (see note above).
export const HEAT_PUMP_25C_CREDIT_USD = 0;
// Modern heat pump also replaces older AC; ~25% less cooling energy.
export const HEAT_PUMP_COOLING_EFFICIENCY_GAIN = 0.25;
export const HEAT_PUMP_COP = 3.0;
export const HEAT_PUMP_GROSS_USD = 16000;

// Regional heating demand curves (HDD-shaped), Jan-Dec, each sums to 1.
// Distributes fossil therms across the year, attributes winter excess kWh,
// and shapes the electric-heat blend.
export const HEATING_MONTH_WEIGHTS_BY_REGION: Record<ClimateRegion, number[]> = {
  COLD: [0.19, 0.16, 0.13, 0.08, 0.03, 0.01, 0, 0, 0.02, 0.06, 0.12, 0.2],
  HOT_DRY: [0.22, 0.17, 0.12, 0.05, 0.01, 0, 0, 0, 0, 0.04, 0.14, 0.25],
  HOT_HUMID: [0.24, 0.18, 0.11, 0.04, 0.01, 0, 0, 0, 0, 0.03, 0.13, 0.26],
  MARINE: [0.17, 0.15, 0.13, 0.1, 0.05, 0.02, 0.01, 0.01, 0.02, 0.07, 0.11, 0.16],
  MIXED: [0.2, 0.17, 0.12, 0.06, 0.02, 0, 0, 0, 0.01, 0.06, 0.13, 0.23],
};

// 25C insulation credit expired Dec 31, 2025 (see note above).
export const INSULATION_CREDIT_CAP_USD = 1200;
export const INSULATION_CREDIT_RATE = 0;
export const INSULATION_GROSS_USD = 5500;
export const INSULATION_HVAC_REDUCTION = 0.15;

export const KWH_PER_THERM = 29.3;

// Scenario electric bills never drop below the utility fixed charge.
export const MONTHLY_FIXED_CHARGE_USD = 12;

// Heating oil cost per therm-equivalent (~$3.90/gal, 1.48 therm/gal).
export const OIL_USD_PER_THERM = 2.4;

export const PAYBACK_CAP_YEARS = 25;

// Flat annual utility rate escalation applied to future-year savings.
export const RATE_ESCALATION = 0.03;

export const SMART_HOME_BASELOAD_REDUCTION = 0.02;
export const SMART_HOME_GROSS_USD = 800;
export const SMART_HOME_HVAC_REDUCTION = 0.08;

// PVWatts-style annual production per kW-DC by region.
export const SOLAR_ANNUAL_KWH_PER_KW_BY_REGION: Record<ClimateRegion, number> = {
  COLD: 1250,
  HOT_DRY: 1650,
  HOT_HUMID: 1500,
  MARINE: 1150,
  MIXED: 1400,
};
// Surplus solar is credited below retail (post-NEM-3 national stance).
export const SOLAR_EXPORT_RATE_FACTOR = 0.75;
// 25D residential ITC expired Dec 31, 2025 (see note above).
export const SOLAR_ITC_RATE = 0;
export const SOLAR_MAX_KW = 12;
export const SOLAR_MIN_KW = 3;
// PVWatts-style monthly production shapes per region, Jan-Dec, each sums to 1.
// Representative cities: Minneapolis (COLD, winter snow losses), Phoenix
// (HOT_DRY, flattest year-round), Houston (HOT_HUMID, summer storms damp the
// peak), Seattle (MARINE, strong winter cloud layer), St. Louis (MIXED).
export const SOLAR_MONTH_WEIGHTS_BY_REGION: Record<ClimateRegion, number[]> = {
  COLD: [0.045, 0.06, 0.085, 0.1, 0.11, 0.115, 0.12, 0.11, 0.09, 0.07, 0.05, 0.045],
  HOT_DRY: [0.06, 0.065, 0.085, 0.095, 0.1, 0.105, 0.1, 0.1, 0.09, 0.08, 0.065, 0.055],
  HOT_HUMID: [0.055, 0.065, 0.085, 0.095, 0.105, 0.105, 0.105, 0.1, 0.09, 0.08, 0.06, 0.055],
  MARINE: [0.03, 0.045, 0.075, 0.1, 0.12, 0.13, 0.135, 0.12, 0.1, 0.075, 0.04, 0.03],
  MIXED: [0.05, 0.06, 0.085, 0.1, 0.11, 0.115, 0.115, 0.11, 0.09, 0.075, 0.05, 0.04],
};
// Fraction of production consumed on-site as it is generated.
export const SOLAR_SELF_CONSUMPTION_FRACTION = 0.45;
export const SOLAR_USD_PER_WATT = 2.8;

// When a zip's climate region differs from its state's default (LA vs marine
// CA, Tahoe vs the coast), the statewide EIA usage shape is blended this far
// toward the override region's mean shape.
export const SUBSTATE_SHAPE_BLEND = 0.5;

// Battery arbitrage value proxy: TOU peak/off-peak spread as a fraction of
// the average retail rate (~1.5-2x peak/off-peak ratios).
export const TOU_SPREAD_FACTOR = 0.4;
